// SELECT YOUR CONTRIBUTION — expandable mission contribution cards for industry partners.
import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Package, Coins, MapPin, Video, Brain, GraduationCap, DoorOpen, ChevronDown } from "lucide-react";

const CONTRIBUTIONS = [
  {
    icon: Package, title: "Supply the Mission",
    body: "Provide products that can be photographed, styled, reviewed, demonstrated, modeled, filmed, or incorporated into an approved creative campaign.",
    activates: ["Makeup Artist", "Hair Stylist", "Photographer", "Model", "Videographer", "Graphic Designer", "Social Media Creator", "Copywriter", "Creative Director"],
  },
  {
    icon: Coins, title: "Fund the Mission",
    body: "Provide financial sponsorship for production expenses, student resources, transportation, locations, printing, equipment, or campaign execution.",
    activates: ["Every unit — funding powers the whole operation", "Photography", "Film", "Design", "Styling", "Social Media"],
  },
  {
    icon: MapPin, title: "Host the Mission",
    body: "Provide a hotel, restaurant, venue, studio, retail location, office, or other approved space for a creative project.",
    activates: ["Photographer", "Videographer", "Model", "Stylist", "Social Media Creator", "Writer", "Creative Director"],
  },
  {
    icon: Video, title: "Equip the Mission",
    body: "Provide cameras, lighting, software, computers, production tools, printing resources, beauty tools, or other equipment.",
    activates: ["Photographer", "Videographer", "Editor", "Graphic Designer", "Beauty Professional", "Social Media Creator"],
  },
  {
    icon: Brain, title: "Brief the Mission",
    body: "Provide a real or simulated creative brief for cohort members to solve.",
    activates: ["Creative Director", "Copywriter", "Graphic Designer", "Photographer", "Social Media Creator", "Videographer"],
  },
  {
    icon: GraduationCap, title: "Teach the Mission",
    body: "Provide mentors, workshops, portfolio reviews, demonstrations, or professional education.",
    activates: ["All disciplines — knowledge deploys everywhere"],
  },
  {
    icon: DoorOpen, title: "Open the Door",
    body: "Provide internships, shadowing opportunities, freelance opportunities, auditions, interviews, introductions, or employment pathways where appropriate.",
    activates: ["Photographer", "Filmmaker", "Designer", "Stylist", "Model", "Writer", "Marketer", "Beauty Professional"],
  },
];

export const ContributionGrid = () => {
  const reduced = useReducedMotion();
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div className="grid gap-4 sm:grid-cols-2" data-testid="contribution-grid">
      {CONTRIBUTIONS.map((c, i) => {
        const Icon = c.icon;
        const open = openIdx === i;
        return (
          <div key={c.title} className={`border bg-charcoal/70 transition-colors duration-300 ${open ? "border-gold/60 shadow-[0_0_30px_rgba(201,162,39,0.1)]" : "border-silver/20 hover:border-gold/40"}`}>
            <button
              onClick={() => setOpenIdx(open ? null : i)}
              aria-expanded={open}
              data-testid={`contribution-${i}`}
              className="flex w-full items-center justify-between gap-4 p-5 text-left"
            >
              <span className="flex items-center gap-4">
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center border transition-colors ${open ? "border-gold bg-gradient-to-b from-goldlight to-gold text-onyx" : "border-gold/40 text-gold"}`}>
                  <Icon size={20} strokeWidth={1.6} />
                </span>
                <span className="font-head text-base font-extrabold uppercase tracking-[0.08em] text-cream sm:text-lg">{c.title}</span>
              </span>
              <ChevronDown size={16} className={`shrink-0 text-gold transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: reduced ? 0 : 0.35, ease: [0.2, 0.8, 0.2, 1] }}
                  className="overflow-hidden"
                >
                  <div className="border-t border-gold/15 p-5">
                    <p className="font-body text-sm leading-relaxed text-silver/85">{c.body}</p>
                    <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.3em] text-gold">This mission may activate:</p>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {c.activates.map((u) => (
                        <span key={u} className="border border-silver/25 bg-onyx px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] text-silver/80">{u}</span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        );
      })}
    </div>
  );
};
